// hooks/academicPlan/usePlanCore.ts
/**
 * Core academic plan hook
 * Manages state, loading, and backend communication
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../useToast';
import { typedApi } from '../../services/typedApiClient';
import { transformCoursesData } from './helpers';
import { dispatchDegreeAuditRefresh } from '../../utils/appEvents';
import { toDomainCourse } from '../../adapters/plan';
import type { ApiCourse } from '../../adapters/plan';
import type { AcademicPlan, SemestersMap, EnrolledCourse, OptimizationStatus, StrategicRecommendation } from '../../types';
import { getErrorMessage } from '../../utils/errorUtils';

// =============================================================================
// HELPERS
// =============================================================================

/** Check if an error is a course-not-found error (should be shown as toast, not global error) */
export const isCourseNotFoundError = (err: unknown): boolean => {
  const message = getErrorMessage(err);
  return message.includes('Course') && message.includes('not found');
};

// =============================================================================
// TYPES
// =============================================================================

/**
 * Default empty plan structure used for initialization
 */
const DEFAULT_EMPTY_PLAN: AcademicPlan = {
  id: 0,
  plan_name: '',
  is_active: false,
  semesters: {},
  bookmarkedCourses: [],
  majors: [],
  minors: [],
  citations: [],
};

/**
 * Parse a raw optimization_status object (from backend JSON) into a typed
 * OptimizationStatus.  Used by both formatPlanFromApi and the optimistic
 * mergeSuggestionsLocally path.
 */
export const formatOptimizationStatus = (
  rawStatus: Record<string, unknown> | undefined,
): OptimizationStatus | null => {
  if (!rawStatus) return null;

  const allRecs = (rawStatus.recommendations as StrategicRecommendation[] | undefined) || [];

  return {
    state: rawStatus.state as OptimizationStatus['state'],
    lastComputedAt: (rawStatus.last_computed_at as string | null) ?? null,
    computationTimeMs: (rawStatus.computation_time_ms as number | null) ?? null,
    suggestionCount: rawStatus.suggestion_count as number | undefined,
    unfulfilledCount: rawStatus.unfulfilled_count as number | undefined,
    strategy: rawStatus.strategy as string | undefined,
    error: (rawStatus.error as string | null) ?? null,
    recommendationCount: rawStatus.recommendation_count as number | undefined,
    criticalRecommendationCount: rawStatus.critical_recommendation_count as number | undefined,
    recommendations: allRecs,
  };
};

/**
 * Transform raw API plan data into AcademicPlan format.
 * Handles:
 * - Response unwrapping ({ plan: {...} } vs {...})
 * - ID normalization (plan_id → id)
 * - Field name conversion (bookmarked_courses → bookmarkedCourses)
 * - Semester transformation via transformCoursesData
 */
export const formatPlanFromApi = (planData: unknown): AcademicPlan => {
  // Extract the actual plan from the API response structure
  const planResponse = planData as { plan?: Record<string, unknown> } & Record<string, unknown>;
  const actualPlan = (planResponse.plan || planResponse) as Record<string, unknown>;

  // Transform courses data from backend format to frontend format
  const transformedSemesters = transformCoursesData(actualPlan) as SemestersMap;

  // Extract optimization status if present (from ?include_suggestions=true)
  const rawStatus = actualPlan.optimization_status as Record<string, unknown> | undefined;
  const optimizationStatus = formatOptimizationStatus(rawStatus);

  return {
    ...DEFAULT_EMPTY_PLAN,
    ...(actualPlan as Partial<AcademicPlan>),
    id: (actualPlan.id as number) || (actualPlan.plan_id as number),
    semesters: transformedSemesters,
    bookmarkedCourses: ((actualPlan.bookmarked_courses || actualPlan.bookmarkedCourses) as EnrolledCourse[]) || [],
    optimizationStatus,
    courseRoles: (actualPlan.course_roles as Record<string, string[]>) || {},
  };
};

/**
 * Return type for the usePlanCore hook
 */
/** Shape of the suggestions payload sent via SSE for optimistic merge */
export interface SuggestionsPayload {
  suggestions: ApiCourse[];
  optimization_status: Record<string, unknown>;
}

export interface UsePlanCoreReturn {
  // State
  academicPlan: AcademicPlan;
  setAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  saving: boolean;
  currentPlanId: number | null;

  // Core operations
  savePlanToBackend: (planId: number, action: string, data: Record<string, unknown>) => Promise<unknown>;
  fetchAcademicPlan: () => Promise<void>;
  createNewPlan: () => Promise<AcademicPlan | undefined>;
  refreshPlan: () => Promise<void>;
  clearSuggestionsLocally: () => void;
  mergeSuggestionsLocally: (payload: SuggestionsPayload) => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Core hook for academic plan state management
 * @param activePlanId - The ID of the currently active plan
 * @returns Core plan state and operations
 */
export const usePlanCore = (activePlanId: number | null = null): UsePlanCoreReturn => {
  const { user } = useAuth();
  const { syncingUser } = useApp();
  const { showError } = useToast();

  // Initialize with empty structure - semesters will be loaded from backend
  const [academicPlan, setAcademicPlan] = useState<AcademicPlan>(DEFAULT_EMPTY_PLAN);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Track the current plan ID
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);

  // Once a plan has loaded successfully, never show the full-page spinner again.
  // Using a ref so it survives across effect re-runs without causing re-renders.
  const hasEverLoaded = useRef(false);

  // Ensure the transfer (AP Credits / 000008) semester exists for a plan.
  //
  // Takes planId as an explicit argument rather than reading currentPlanId.
  // The callers (fetch/create) set currentPlanId via setCurrentPlanId() in the
  // SAME async tick they invoke this, so the state (and any ref mirroring it)
  // is still stale here — passing the id the caller already holds avoids that
  // race. Keeping currentPlanId out of this callback's deps also means a
  // plan-id change doesn't recreate fetchAcademicPlan and re-fire its effect
  // (which would cause a double fetch).
  const ensureTransferSemester = useCallback(async (planId: number | null) => {
    if (!planId || !user?.id) return;

    try {
      await typedApi.transferCredits.ensureSemester(planId);
    } catch {
      // Silent fail - transfer semester is optional
    }
  }, [user?.id]);

  // Save plan to backend (with better error handling)
  const savePlanToBackend = useCallback(async (
    planId: number,
    action: string,
    data: Record<string, unknown>
  ): Promise<unknown> => {
    if (!planId) {
      return;
    }

    // REQUIRE authentication
    if (!user?.id) {
      throw new Error('Authentication required');
    }

    try {
      setSaving(true);

      const result = await typedApi.academicPlans.update(planId, action, {
        ...data
      });

      return result;
    } catch (err) {
      const error = err as Error & { isCourseNotFound?: boolean };
      // Only set global error for non-course-specific errors
      if (!error.isCourseNotFound && !isCourseNotFoundError(error)) {
        setError(error.message);
      }
      throw err;
    } finally {
      setSaving(false);
    }
  }, [user]);

  // Fetch academic plan from backend
  const fetchAcademicPlan = useCallback(async () => {
    try {
      // After the first successful load, use refreshing (inline indicator)
      // instead of loading (full-page spinner) to avoid blank-page flashes.
      if (hasEverLoaded.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // REQUIRE authentication
      if (!user?.id) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // If we have an activePlanId, fetch that specific plan
      if (activePlanId) {
        let planData;
        try {
          planData = await typedApi.academicPlans.get(activePlanId);
        } catch (err) {
          const error = err as Error;
          // Don't throw course-specific errors that would crash the app
          if (isCourseNotFoundError(error)) {
            showError(error.message);
            setLoading(false);
            return;
          }
          throw err;
        }

        const formattedPlan = formatPlanFromApi(planData);
        // The backend payload is authoritative — persisted placeholders
        // (#205) arrive IN it, so a wholesale replacement is safe even on
        // the mid-session re-fires (auth token refresh, clearAllCourses
        // error recovery). Do NOT re-add a client-side placeholder carry:
        // it would resurrect rows the backend deleted (the PR #233 ghost).
        setAcademicPlan(formattedPlan);
        setCurrentPlanId(activePlanId);
        hasEverLoaded.current = true;
        setError(null);

        // Ensure transfer semester exists after plan loads
        // Skip if plan has no semesters (e.g., after data reset) — the AP Credits
        // semester will be created when the user sets up their plan via onboarding
        if (Object.keys(formattedPlan.semesters || {}).length > 0) {
          await ensureTransferSemester(activePlanId);
        }

        return;
      }

      // Otherwise, fetch all plans for the authenticated user
      const data = await typedApi.academicPlans.list();

      // Check if we have plans - the API might return them in different formats
      const plans = (data as { plans?: AcademicPlan[] }).plans || data;

      if (Array.isArray(plans) && plans.length > 0) {
        // Get the active plan or the first plan
        const activePlan = plans.find(p => p.is_active) || plans[0];
        setCurrentPlanId(activePlan.id);

        // Fetch detailed plan data
        let planData;
        try {
          planData = await typedApi.academicPlans.get(activePlan.id);
        } catch (err) {
          const error = err as Error;
          // Don't throw course-specific errors
          if (error.message && error.message.includes('Course') && error.message.includes('not found')) {
            showError(error.message);
            setLoading(false);
            return;
          }
          throw err;
        }

        const formattedPlan = formatPlanFromApi(planData);
        // Payload-is-truth, same as the activePlanId branch above (this
        // branch runs when no explicit activePlanId is set).
        setAcademicPlan(formattedPlan);
        hasEverLoaded.current = true;
        setError(null);

        // Ensure transfer semester exists after plan loads
        // Skip if plan has no semesters (e.g., after data reset)
        if (Object.keys(formattedPlan.semesters || {}).length > 0) {
          await ensureTransferSemester(activePlan.id);
        }
      } else {
        // No plans exist, create a new one
        await createNewPlan();
      }
    } catch (err) {
      const error = err as Error;
      // Don't set global error for course-specific issues
      if (isCourseNotFoundError(error)) {
        showError(error.message);
        setLoading(false);
        return;
      }

      setError(error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activePlanId, showError, ensureTransferSemester]);

  // Create a new academic plan
  const createNewPlan = useCallback(async (): Promise<AcademicPlan | undefined> => {
    try {
      // REQUIRE authentication
      if (!user?.id) {
        throw new Error('Authentication required to create a plan');
      }

      const body = {
        plan_type: 'primary',
        majors: [],
        minors: []
      };

      const data = await typedApi.academicPlans.create(body as Parameters<typeof typedApi.academicPlans.create>[0]);

      const createdPlanResponse = data as { plan: { id: number } & Record<string, unknown> };
      const createdPlan = createdPlanResponse.plan;
      setCurrentPlanId(createdPlan.id);

      // Fetch the newly created plan
      const planData = await typedApi.academicPlans.get(createdPlan.id);

      const formattedPlan = formatPlanFromApi(planData);
      setAcademicPlan(formattedPlan);

      // Ensure transfer semester exists after plan creation
      // Skip if plan has no semesters (new empty plan)
      if (Object.keys(formattedPlan.semesters || {}).length > 0) {
        await ensureTransferSemester(createdPlan.id);
      }

      return createdPlan as unknown as AcademicPlan;
    } catch (err) {
      throw err;
    }
  }, [user, ensureTransferSemester]);

  // Load academic plan on mount and when activePlanId changes
  // Wait for user sync to complete before fetching to avoid race condition
  useEffect(() => {
    // Don't fetch until sync is complete - prevents 401/404 errors for new users
    if (syncingUser) {
      setLoading(true);
      return;
    }

    if (user?.id) {
      fetchAcademicPlan();
    } else {
      // Clear state when no user
      setAcademicPlan(DEFAULT_EMPTY_PLAN);
      setCurrentPlanId(null);
      setLoading(false);
      setError('Authentication required');
    }
  }, [fetchAcademicPlan, activePlanId, user?.id, syncingUser]);

  // Poll for optimization completion when status is 'computing' or 'stale'
  // ('stale' means a recompute was triggered but hasn't completed yet).
  // Uses the lightweight /optimizer/status endpoint to avoid full plan fetches,
  // then does one full refresh when optimization reaches a terminal state.
  useEffect(() => {
    const status = academicPlan.optimizationStatus?.state;
    if ((status !== 'computing' && status !== 'stale') || !currentPlanId) {
      return;
    }

    // Hard cap on polling. A real recompute reaches a terminal state in seconds;
    // if the status is stuck non-terminal (e.g. a backend recompute that never
    // completes), we must NOT poll forever — that turns into a request storm
    // that hammers /optimizer/status and starves the rest of the app. Stop after
    // MAX_POLL_ATTEMPTS (~2 min) regardless.
    const MAX_POLL_ATTEMPTS = 60;
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts += 1;
      try {
        const statusData = await typedApi.optimizer.getStatus(currentPlanId);
        const newState = statusData?.optimization_status?.state as string | undefined;

        if (newState && newState !== 'computing' && newState !== 'stale') {
          // Terminal state reached — do one full plan refresh to load suggestions
          clearInterval(pollInterval);
          const planData = await typedApi.academicPlans.get(currentPlanId);
          // Payload-is-truth (#205): the poll's replacement carries any
          // persisted placeholders itself.
          setAcademicPlan(formatPlanFromApi(planData));
          return;
        }
      } catch (err) {
        console.warn('[usePlanCore] Optimization poll failed:', err);
      }

      if (attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(pollInterval);
        console.warn(
          '[usePlanCore] Optimization status still non-terminal after max attempts; stopping poll.'
        );
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [academicPlan.optimizationStatus?.state, currentPlanId]);

  // Refresh plan from backend (without full-page loading spinner)
  const refreshPlan = useCallback(async () => {
    const planId = currentPlanId || activePlanId;
    if (!planId || !user?.id) return;

    try {
      setRefreshing(true);
      const planData = await typedApi.academicPlans.get(planId);
      const formattedPlan = formatPlanFromApi(planData);
      // Placeholders persist server-side (#205) and arrive in this payload
      // — the original UC Merced QA loss ("adding a course makes the
      // placeholder disappear") is closed at the source, not by a client
      // carry. Replacement IS refresh-to-truth.
      setAcademicPlan(formattedPlan);
      dispatchDegreeAuditRefresh(planId);
    } catch (err) {
      const error = err as Error;
      if (isCourseNotFoundError(error)) {
        showError(error.message);
      } else {
        console.warn('[usePlanCore] refreshPlan failed:', err);
      }
    } finally {
      setRefreshing(false);
    }
  }, [currentPlanId, activePlanId, user?.id, showError]);

  // Optimistically strip suggestions from local state (used by clear_suggestions)
  const clearSuggestionsLocally = useCallback(() => {
    setAcademicPlan(prev => ({
      ...prev,
      semesters: Object.fromEntries(
        Object.entries(prev.semesters).map(([k, courses]) => [
          k, courses.filter(c => !c.isSuggestion),
        ])
      ) as SemestersMap,
      optimizationStatus: { state: 'idle' as const } as OptimizationStatus,
    }));
  }, []);

  // Optimistically merge new suggestions into local state (used by generate_suggestions).
  // Mirrors clearSuggestionsLocally but adds courses instead of removing them.
  const mergeSuggestionsLocally = useCallback((payload: SuggestionsPayload) => {
    setAcademicPlan(prev => {
      // Build pin codes for dedup (only pinned/real courses, not old suggestions)
      const pinCodes = new Set<string>();
      for (const courses of Object.values(prev.semesters)) {
        for (const c of courses) {
          if (!c.isSuggestion) pinCodes.add(c.code);
        }
      }

      // Group suggestions by semester key, converting via toDomainCourse
      const suggestionsBySemester: Record<string, EnrolledCourse[]> = {};
      for (const raw of payload.suggestions) {
        const course = toDomainCourse(raw);
        if (!course || pinCodes.has(course.code)) continue;
        const key = String(raw.display_order ?? 0).padStart(6, '0');
        if (!suggestionsBySemester[key]) suggestionsBySemester[key] = [];
        suggestionsBySemester[key].push(course);
      }

      // Strip old suggestions, then merge new ones
      const merged: Record<string, EnrolledCourse[]> = {};
      for (const [k, courses] of Object.entries(prev.semesters)) {
        merged[k] = courses.filter(c => !c.isSuggestion);
      }
      for (const [k, suggestions] of Object.entries(suggestionsBySemester)) {
        merged[k] = [...(merged[k] || []), ...suggestions];
      }

      return {
        ...prev,
        semesters: merged as SemestersMap,
        optimizationStatus: formatOptimizationStatus(payload.optimization_status) ??
          { state: 'complete' as const } as OptimizationStatus,
      };
    });
  }, []);

  return {
    // State
    academicPlan,
    setAcademicPlan,
    loading,
    refreshing,
    error,
    saving,
    currentPlanId,

    // Core operations
    savePlanToBackend,
    fetchAcademicPlan,
    createNewPlan,
    refreshPlan,
    clearSuggestionsLocally,
    mergeSuggestionsLocally,
  };
};
